import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useT } from '../lib/i18n'
import { ErrorBanner, Icon, Spinner } from '../components/ui'
import { stillMissing, type AskDraft } from '../components/askDraft'
import type { City } from '../components/CityPicker'
import { useAsk, type Action, type ActionStep, type Draft } from './ask'
import { AskJourney } from './AskJourney'
import { useKeyboardInset } from './LiquidTabBar'

/**
 * Ask about your own data -- the answers.
 *
 * A PANEL THAT RISES ABOVE THE TAB BAR, where the compose box now lives (it is
 * what the Ask button turns into). The box stays in the bar, at the bottom,
 * above the keyboard; the answers stack up from just above it, so the newest
 * one is always next to the question that produced it.
 *
 * The suggestions are the feature, not decoration: this is a lookup tool with
 * a fixed vocabulary, so showing what it can answer is the difference between
 * useful and a text box that says "I do not understand".
 *
 * `understood` is always displayed. When the answer is wrong, seeing what it
 * thought you asked is what makes it diagnosable instead of baffling.
 */
export function AskPanel() {
  const msg = useT()
  const nav = useNavigate()
  const { open, setOpen, journey, setJourney, send, history, ask, suggestions } = useAsk()
  const end = useRef<HTMLDivElement>(null)
  const inset = useKeyboardInset()

  // Keep the newest answer in view as the conversation grows.
  useEffect(() => { end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }) }, [history.length, ask.isPending])

  const go = (to: string, state?: unknown) => { setOpen(false); nav(to, state ? { state } : undefined) }

  const chips = (suggestions.data?.suggestions ?? [])
    .filter((sg) => !history.some((h) => h.q === sg.text))

  return (
    <>
      {/* The mockup's journey sheet, beside the lookup panel: two different
          conversations, only one open at a time. */}
      <AskJourney open={journey} onClose={() => setJourney(false)} />

      {/* Tap anywhere above the panel to fold the bar back up. */}
      <div className={`askpanel-backdrop ${open ? 'is-open' : ''}`} aria-hidden onClick={() => setOpen(false)} />
      <section
        className={`askpanel ${open ? 'is-open' : ''}`}
        aria-label={msg('askBubble.askAboutYourData')}
        aria-hidden={!open}
        style={inset ? { transform: `translateY(-${inset}px)` } : undefined}
      >
        <div className="askpanel-scroll">
          <p className="label mb-2 px-1">
            {msg('askBubble.answersFromYourOwnData')}
          </p>

          {history.length === 0 && (
            <div className="flex flex-wrap gap-1.5">
              {chips.map((sg) => (
                <button key={sg.key} type="button" onClick={() => send(sg.text)} tabIndex={open ? 0 : -1}
                        className="askchip">
                  {msg(sg.key, { fallback: sg.text })}
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-3">
            {history.map((turn, i) => (
              <div key={i} className="flex flex-col gap-2">
                <p className="ms-auto max-w-[85%] rounded-2xl rounded-ee-md bg-accent px-4 py-2.5
                              text-[14.5px] text-white">
                  {turn.q}
                </p>
                {turn.a.type === 'draft'
                  ? <DraftCard draft={turn.a} onGo={(to, d) => go(to, { draft: d })} />
                  : turn.a.type === 'action'
                  ? <ActionCard action={turn.a} />
                  : ((answer) => (
                    <div className="me-auto max-w-[92%] rounded-2xl rounded-es-md bg-surface px-4 py-3
                                    shadow-card">
                      <p className="label mb-2">{answer.understood}</p>
                      <ul className="flex flex-col gap-1.5">
                        {answer.bullets.map((b, j) => (
                          <li key={j} className="flex gap-2 text-[14px] leading-snug">
                            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                            <span className="selectable">{b}</span>
                          </li>
                        ))}
                      </ul>
                      {answer.note && (
                        <p className="mt-2 text-[12.5px] text-ink-3">{answer.note}</p>
                      )}
                      {answer.link && (
                        <button type="button" className="btn-ghost mt-3 !min-h-[44px] !text-[14px]"
                                onClick={() => go(answer.link!.to)}>
                          {answer.link.label}
                        </button>
                      )}
                    </div>
                  ))(turn.a)}

                {/* Chips again after every answer. A conversation that ends in
                    a full stop makes the person think of the next question
                    themselves; this hands them four. */}
                {i === history.length - 1 && !ask.isPending && (
                  <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1
                                  [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {chips.slice(0, 5).map((sg) => (
                      <button key={sg.key} type="button" onClick={() => send(sg.text)}
                              className="askchip shrink-0">
                        {msg(sg.key, { fallback: sg.text })}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {ask.isPending && <Spinner label={msg('askBubble.looking')} />}
            {ask.isError && <ErrorBanner error={ask.error} />}
            <div ref={end} />
          </div>
        </div>
      </section>
    </>
  )
}

/**
 * A prepared carrier action, and the press that performs it.
 *
 * THE SENTENCE DID NOTHING. The server resolved the words into the carrier's
 * own rows and described what WOULD happen; this draws that description and
 * the button that calls the endpoint. The two events -- saying it and doing it
 * -- are deliberately not the same event, the same rule the passenger side
 * follows for a booking draft (AskJourney: "Nothing is sent yet", then
 * "Confirm and send").
 *
 * WHEN THE SENTENCE WAS AMBIGUOUS THERE ARE SEVERAL CARDS, each fully prepared
 * and each with its own button, under the question the server asked. The
 * server never picks one, and neither does this: a carrier with two coaches
 * leaving at 06:40 presses the one he meant rather than finding out afterwards
 * which one the computer chose.
 */
function ActionCard({ action }: { action: Action }) {
  // No catalogue lookups here on purpose: every word in a prepared action --
  // the title, the field labels, the confirm button, the "done" line -- is
  // worded by the server, from the same catalogue, because only the server
  // knows what it resolved. The card is a frame around the server's sentences.
  if (action.blocked?.length) {
    return (
      <div className="me-auto max-w-[92%] rounded-2xl rounded-es-md bg-surface px-4 py-3 shadow-card">
        <p className="label mb-2">{action.understood}</p>
        <ul className="flex flex-col gap-1.5">
          {action.blocked.map((b, i) => (
            <li key={i} className="flex gap-2 text-[14px] leading-snug">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-band-amber" />
              <span className="selectable">{b}</span>
            </li>
          ))}
        </ul>
        {action.note && <p className="mt-2 text-[12.5px] text-ink-3">{action.note}</p>}
      </div>
    )
  }

  return (
    <div className="me-auto flex w-[92%] flex-col gap-2">
      <p className="label px-1">{action.understood}</p>
      {action.choose && (
        <p className="px-1 font-display text-[14px] font-bold">{action.choose}</p>
      )}
      {action.steps.map((step, i) => <Step key={i} step={step} />)}
      {action.note && <p className="px-1 text-[12.5px] text-ink-3">{action.note}</p>}
    </div>
  )
}

/** One prepared thing: what it will change, and the one press that changes it. */
function Step({ step }: { step: ActionStep }) {
  const msg = useT()
  const qc = useQueryClient()

  const run = useMutation({
    mutationFn: () => api<unknown>(step.call.path, {
      method: step.call.method,
      body: step.call.body,
    }),
    onSuccess: () => {
      // Everything a carrier action can touch. Cheaper than reasoning about
      // which list a given action invalidated, and a stale fleet board after
      // closing a departure is exactly the confusion this feature exists to
      // remove.
      for (const key of ['dashboard', 'trips', 'trips-board', 'requests',
                         'quotations', 'vehicles', 'drivers', 'orders']) {
        void qc.invalidateQueries({ queryKey: [key] })
      }
    },
  })

  if (run.isSuccess) {
    return (
      <div className="rounded-2xl border border-band-green/30 bg-band-green-wash px-4 py-4">
        <div className="flex items-center gap-2">
          <Icon name="check" className="h-5 w-5 text-band-green" />
          <p className="font-display text-[15px] font-extrabold text-band-green">
            {msg('ask.act.doneTitle')}
          </p>
        </div>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-2">{step.done}</p>
      </div>
    )
  }

  return (
    <div className="rounded-2xl bg-surface px-4 py-3 shadow-card ring-1 ring-accent/20">
      <p className="font-display text-[14px] font-bold">{step.title}</p>
      <dl className="mt-2 flex flex-col gap-1.5 text-[14px]">
        {step.lines.map((l, i) => (
          <div key={i} className="flex items-baseline justify-between gap-3">
            <dt className="shrink-0 text-ink-3">{l.label}</dt>
            <dd className="text-end font-semibold">{l.value}</dd>
          </div>
        ))}
      </dl>
      {run.isError && <ErrorBanner error={run.error} />}
      <button type="button" className="btn-primary mt-3 !min-h-[46px] !text-[14px]"
              disabled={run.isPending}
              onClick={() => run.mutate()}>
        {run.isPending ? msg('ask.act.doing') : step.confirm}
      </button>
    </div>
  )
}

/**
 * A prepared request, the questions that finish it, and the button that sends
 * it.
 *
 * ONE QUESTION AT A TIME, in the order a person can answer: the day (which
 * depends on nothing), where from, where to (which can only be offered once
 * the origin is known -- the list is "places something really runs to from
 * here"), and what is being sent.
 *
 * IT SENDS NOTHING UNTIL TOLD TO. When the last gap is filled it shows the
 * whole thing back and waits for one deliberate press. A sentence is a request
 * to prepare; the press is the authority to act, and those two must not be the
 * same event.
 *
 * A SEAT BOOKING IS THE EXCEPTION and stops one step short. It spends money on
 * a particular seat, and the seat map is where the price and what is still
 * free are shown -- so this gathers the details and hands over rather than
 * choosing somebody's seat for them.
 */
function DraftCard({ draft, onGo }: {
  draft: Draft
  onGo: (to: string, draft: AskDraft) => void
}) {
  const msg = useT()
  const qc = useQueryClient()

  const [d, setD] = useState<AskDraft>(draft)
  useEffect(() => { setD(draft) }, [draft])

  const send = useMutation({
    mutationFn: () => api<{ id: string; request_no: string }>('/requests', {
      method: 'POST',
      body: {
        from_city_id: d.from!.id,
        to_city_id: d.to!.id,
        preferred_date: d.date ?? null,
        flexible_days: 0,
        // No vehicle picked here, so the server asks everyone on the route
        // with room -- the path that brings back the most prices.
        target_transporter_ids: [],
        items: (d.items ?? []).map((i) => ({
          item_type_id: i.item_type_id,
          qty: i.qty,
          // The catalogue's own defaults. The person can correct sizes on the
          // request itself; asking for centimetres in a chat would be worse
          // than a sensible starting point.
          l_cm: 40, w_cm: 30, h_cm: 30, weight_kg: 5,
        })),
      },
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
      void qc.invalidateQueries({ queryKey: ['requests'] })
    },
  })

  const gaps = stillMissing(d)
  const asking = gaps[0]

  // ------------------------------------------------------------- sent
  if (send.isSuccess) {
    return (
      <div className="me-auto w-[92%] rounded-2xl rounded-es-md border border-band-green/30
                      bg-band-green-wash px-4 py-4">
        <div className="flex items-center gap-2">
          <Icon name="check" className="h-5 w-5 text-band-green" />
          <p className="font-display text-[15px] font-extrabold text-band-green">
            {msg('ask.sentTitle')}
          </p>
        </div>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-2">
          {msg('ask.sentBody', { number: send.data.request_no })}
        </p>
        <button type="button" className="btn-primary mt-3 !min-h-[46px] !text-[14px]"
                onClick={() => onGo(`/requests/${send.data.id}`, d)}>
          {msg('ask.viewRequest')}
        </button>
      </div>
    )
  }

  return (
    <div className="me-auto w-[92%] rounded-2xl rounded-es-md bg-surface px-4 py-3 shadow-card
                    ring-1 ring-accent/20">
      <p className="label mb-2">{d.understood}</p>

      <dl className="flex flex-col gap-1.5 text-[14px]">
        <Line label={msg('common.date')}
              value={d.date ? d.date : (d.anyDay ? msg('date.anyDay') : undefined)} />
        <Line label={msg('common.from')} value={d.from?.name} />
        <Line label={msg('common.to')} value={d.to?.name} />
        {d.kind === 'passenger'
          ? <Line label={msg('travel.passengers')} value={String(d.seats ?? 1)} />
          : <Line label={msg('newRequest.whatAreYouSending')}
                  value={d.items?.length
                    ? d.items.map((i) =>
                        `${i.qty} \u00d7 ${msg(`item.${i.code}`, { fallback: i.code })}`).join(', ')
                    : undefined} />}
      </dl>

      {(d.outOfScope?.length ?? 0) > 0 && d.countryName && (
        <p className="mt-3 rounded-xl bg-band-amber-wash px-3 py-2 text-[12.5px] leading-relaxed
                      text-band-amber">
          {msg('ask.onlyCountry', { country: d.countryName })} {msg('ask.movedCountry')}
        </p>
      )}

      {asking === 'date' && (
        <Ask label={msg('ask.whenIsIt')}>
          <DateChips
            onPick={(iso) => setD((p) => ({ ...p, date: iso, anyDay: !iso }))}
          />
        </Ask>
      )}

      {asking === 'from' && (
        <Ask label={msg('ask.whereFrom')}>
          <CityChips onPick={(c) => setD((p) => ({ ...p, from: c, to: undefined }))} />
        </Ask>
      )}

      {asking === 'to' && d.from && (
        <Ask label={msg('ask.whereTo')}>
          <DestinationChips from={d.from} kind={d.kind}
                            onPick={(c) => setD((p) => ({ ...p, to: c }))} />
        </Ask>
      )}

      {asking === 'items' && (
        <Ask label={msg('ask.whatIsIt')}>
          <ItemChips onPick={(i) => setD((p) => ({ ...p, items: [...(p.items ?? []), i] }))} />
        </Ask>
      )}

      {/* Nothing left to ask: show it back, and wait to be told. */}
      {gaps.length === 0 && (
        <div className="mt-3 border-t border-line pt-3">
          {d.kind === 'passenger' ? (
            <>
              <p className="font-display text-[14px] font-bold">{msg('ask.seatsNext')}</p>
              <p className="mb-3 mt-0.5 text-[12.5px] leading-relaxed text-ink-3">
                {msg('ask.seatsNextHint')}
              </p>
              <button type="button" className="btn-primary !min-h-[46px] !text-[14px]"
                      onClick={() => onGo('/travel', d)}>
                {msg('ask.seatsNext')}
              </button>
            </>
          ) : (
            <>
              <p className="font-display text-[14px] font-bold">{msg('ask.reviewTitle')}</p>
              <p className="mb-3 mt-0.5 text-[12.5px] text-ink-3">{msg('ask.reviewHint')}</p>
              {send.isError && <ErrorBanner error={send.error} />}
              <button type="button" className="btn-primary !min-h-[46px] !text-[14px]"
                      disabled={send.isPending}
                      onClick={() => send.mutate()}>
                {send.isPending ? msg('ask.sending') : msg('ask.confirmAndSend')}
              </button>
              <button type="button"
                      className="btn-bare mt-1 w-full !text-[13px]"
                      onClick={() => setD({ ...draft, date: undefined, anyDay: false })}>
                {msg('ask.startAgain')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

/** One question, with its answers underneath. */
function Ask({ label, children }: { label: string; children: ReactNode }) {
  const msg = useT()
  return (
    <div className="mt-3 border-t border-line pt-3">
      <p className="font-display text-[14px] font-bold">{label}</p>
      <p className="mb-2 text-[12px] text-ink-3">{msg('ask.pickOrType')}</p>
      {children}
    </div>
  )
}

function Chips({ children }: { children: ReactNode }) {
  return <div className="-mx-1 flex flex-wrap gap-2 px-1">{children}</div>
}

function Chip({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick}
            className="rounded-full bg-surface-2 px-3.5 py-2 text-[13px] font-semibold
                       text-accent-deep ring-1 ring-accent/20 active:bg-accent-wash">
      {children}
    </button>
  )
}

/** Today, tomorrow, the next few days, or no particular day. */
function DateChips({ onPick }: { onPick: (iso: string) => void }) {
  const msg = useT()
  const today = new Date()
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const plus = (n: number) => {
    const d = new Date(today)
    d.setDate(d.getDate() + n)
    return d
  }

  return (
    <Chips>
      <Chip onClick={() => onPick('')}>{msg('date.anyDay')}</Chip>
      <Chip onClick={() => onPick(iso(today))}>{msg('date.today')}</Chip>
      <Chip onClick={() => onPick(iso(plus(1)))}>{msg('date.tomorrow')}</Chip>
      {[2, 3, 4].map((n) => (
        <Chip key={n} onClick={() => onPick(iso(plus(n)))}>
          {plus(n).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
        </Chip>
      ))}
    </Chips>
  )
}

/** Where this client actually starts from, newest habit first. */
function CityChips({ onPick }: { onPick: (c: City) => void }) {
  const q = useQuery<{ items: City[] }>({
    queryKey: ['cities-suggested-6'],
    queryFn: () => api('/cities/suggested?limit=6'),
    staleTime: 5 * 60_000,
  })
  if (q.isLoading) return <Spinner />
  return (
    <Chips>
      {(q.data?.items ?? []).map((c) => (
        <Chip key={c.id} onClick={() => onPick(c)}>{c.name}</Chip>
      ))}
    </Chips>
  )
}

/**
 * Not a city list: the places something really runs to from here. A free
 * choice would let someone finish a draft on a route nobody drives.
 */
function DestinationChips({ from, kind, onPick }: {
  from: City
  kind: 'freight' | 'passenger'
  onPick: (c: City) => void
}) {
  const msg = useT()
  const q = useQuery<{ id: string; name: string }[]>({
    queryKey: ['draft-destinations', from.id, kind],
    queryFn: () => api(
      `/discover/destinations?from_city_id=${from.id}`
      + (kind === 'passenger' ? '&mode=passenger' : '')),
  })

  if (q.isLoading) return <Spinner />
  if ((q.data?.length ?? 0) === 0) {
    return (
      <p className="text-[13px] text-ink-3">
        {msg('ask.noDestinations', { city: from.name })}
      </p>
    )
  }
  return (
    <Chips>
      {(q.data ?? []).map((c) => (
        <Chip key={c.id}
              onClick={() => onPick({
                id: c.id, name: c.name, admin_area: null, country_iso2: from.country_iso2,
              })}>
          {c.name}
        </Chip>
      ))}
    </Chips>
  )
}

function ItemChips({ onPick }: {
  onPick: (i: { item_type_id: string; code: string; label_key: string; qty: number }) => void
}) {
  const msg = useT()
  const q = useQuery<{ id: string; code: string; label_key: string }[]>({
    queryKey: ['item-types'], queryFn: () => api('/item-types'),
    staleTime: 60 * 60_000,
  })
  if (q.isLoading) return <Spinner />
  return (
    <Chips>
      {(q.data ?? []).map((t) => (
        <Chip key={t.id}
              onClick={() => onPick({
                item_type_id: t.id, code: t.code, label_key: t.label_key, qty: 1,
              })}>
          {msg(`item.${t.code}`, { fallback: t.code })}
        </Chip>
      ))}
    </Chips>
  )
}

function Line({ label, value }: { label: string; value?: string }) {
  const msg = useT()
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-3">{label}</dt>
      <dd className={`text-end font-semibold ${value ? '' : 'text-band-amber'}`}>
        {value || msg('ask.stillNeeded')}
      </dd>
    </div>
  )
}
